import React, { useState, useEffect } from 'react';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputLabel from '../inputLabel/InputLabel';
import { CommonValidation } from '../commonValidation/commonValidation';
import { loadPopovers } from '../dateInput/Popovers';

export const formatNumber = (val: number | undefined, dp: number): string => {
    const value = val === undefined ? 0 : val;
    const formattedValue = new Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp
    }).format(value);
    return formattedValue;
};

export interface NumberInputProps {
    /** The name reference to the component */
    name: string;
    /** Additional styles to wrap onto the component */
    className?: string;
    /** An index of what piece of data is being modified */
    datakey?: string;
    /** An error message to display under the component */
    error?: string;
    /** The label that appears above the component */
    label?: string;
    /** The placeholder text that appears in the textbox */
    placeholder?: string;
    /** The text that appears when hitting the help button */
    helpContent?: string;
    /** The current value of the component */
    value?: string;
    /** Decides if the component is editable */
    readonly?: boolean;
    /** Decides if the component is disabled */
    disabled?: boolean;
    /** Decides if the component is has a help option */
    help?: boolean;
    /** Decides if the component is focused automatically on page load */
    autoFocus?: boolean;
    /**Decides if the labels should have the floating apperance of be outside the component */
    floatingLabel?: boolean;
    /** The amount of decimal places (Default is 2 if not provided) */
    decimalPlaces?: number;
    /** A function to be called when bluring out of the component */
    onLeave?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
    /** A function to specify what should happen when the component is focused */
    onFocus?: () => void;
    /** A function to be called on every key stroke to pass the value back up to it's controller */
    onChange?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
}

/** An input that specifically uses numerics */
const NumberInput = ({
    name,
    className,
    datakey = '',
    error,
    label,
    placeholder,
    helpContent,
    value,
    readonly,
    disabled,
    help,
    floatingLabel,
    autoFocus,
    decimalPlaces = 2,
    onLeave,
    onFocus,
    onChange
}: NumberInputProps): JSX.Element => {
    useEffect(() => {
        loadPopovers();
    }, []);

    const [editing, setEditing] = useState(false);

    const setEditingMode = (editMode: boolean) => {
        setEditing(editMode);
    };

    const onFocusEvent = () => {
        if (onFocus) {
            onFocus();
        }
        setEditingMode(true);
    };

    const onLeaveEvent = (event: React.FormEvent<HTMLInputElement>) => {
        let value = event.currentTarget.value;

        if (!CommonValidation.isEmpty(value)) {
            value = updateValue(value);
            setEditing(false);
        }

        if (onLeave) {
            onLeave(event, datakey);
        }
    };

    const onChangeEvent = (event: React.FormEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event, datakey);
        }
    };

    const updateValue = (value: string) => {
        let controlValue = value;

        const isValid =
            !CommonValidation.isEmpty(controlValue) &&
            CommonValidation.isValidDecimal(controlValue);
        if (isValid) {
            controlValue = controlValue.trim();
            const decimalFixedValue: string =
                parseFloat(controlValue).toFixed(decimalPlaces);
            const decimalValue = parseFloat(controlValue);

            if (parseInt(decimalFixedValue, 10) != decimalValue)
                controlValue = decimalFixedValue;
            else controlValue = decimalValue.toString();
        }

        return controlValue;
    };

    let styleWrapper = 'form-group';
    styleWrapper += error ? ' has-error' : '';

    let inputClasses = 'field';
    inputClasses += className ? ` ${className}` : '';

    let inputInnerStyle = 'form-control';
    if (floatingLabel && label) {
        inputInnerStyle += error ? ' is-invalid' : '';
    }

    const createLabel = (): JSX.Element => {
        if (label !== undefined) {
            return <InputLabel labelText={label} name={name} />;
        }
        return <></>;
    };

    let controlValue = value;

    if (!editing && controlValue) {
        if (CommonValidation.isValidDecimal(controlValue)) {
            const textInputValue = controlValue || '0';
            controlValue = formatNumber(
                parseFloat(textInputValue),
                decimalPlaces
            );
        }
    }

    const createRenderPanel = () => {
        let helpBlock: JSX.Element = <></>;

        if (help) {
            helpBlock = (
                <a
                    id='popoverButton'
                    tabIndex={0}
                    style={{ padding: '0.5rem 0.5rem' }}
                    className='btn btn-sm btn-primary'
                    title={label}
                    data-bs-toggle='popover'
                    data-html='true'
                    data-bs-trigger='focus'
                    data-trigger='focus'
                    data-bs-content={helpContent}>
                    <FontAwesomeIcon icon={faQuestion} />
                </a>
            );
        }

        return (
            <>
                <input
                    id={name}
                    type='text'
                    autoComplete='off'
                    className={inputInnerStyle}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    name={name}
                    value={controlValue}
                    onChange={onChangeEvent}
                    onFocus={onFocusEvent}
                    onBlur={onLeaveEvent}
                    readOnly={readonly}
                    disabled={disabled}
                />
                {helpBlock}
            </>
        );
    };

    const labelControl = createLabel();
    const inputControl = createRenderPanel();

    const createFinalControl = () => {
        if (floatingLabel && label) {
            let wrapper = 'form-floating field ';
            let errorColourStyle = '';

            if (error) {
                wrapper += 'has-error';
                errorColourStyle = 'floating-label-validation-color';
            }

            return (
                <div>
                    <div className={wrapper}>
                        {inputControl}
                        <label htmlFor={name}>{label}</label>
                    </div>
                    <div className={errorColourStyle}>{error}</div>
                </div>
            );
        } else {
            return (
                <div className={styleWrapper}>
                    {labelControl}
                    <div className={inputClasses}>{inputControl}</div>
                    <div className='input'>{error}</div>
                </div>
            );
        }
    };

    const finalControl = createFinalControl();

    return <>{finalControl}</>;
};

export default NumberInput;
